(ns nosco.ui.tooltips
  (:require
   ["react" :as React]
   ["react-useportal$default" :as usePortal]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.react :as nosco-react]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.util :as util]))

(defn interpose-br
  "Returns a React element with the items of `coll` interposed with `<br>` elements."
  [coll]
  (hx/f (into [:<>] (interpose [:br]) coll)))

(defnc Tooltip [{:keys [label popper visible tooltip-style]}]
  (let [style (.. (:popper popper) -styles -popper)
        ;; style is read-only
        style (if tooltip-style
                (js/Object.assign (clj->js tooltip-style) style)
                style)
        style (if (and visible label)
                (js/Object.assign #js{"display" "block"} style) ;; use display:none to ensure off-screen tooltips don't
                (js/Object.assign #js{"display" "none"} style))] ;; contribute to the document width (e.g. in tabs)
    [(.-Portal (usePortal #js {:bindTo (hooks/useContext popup/OverlayContainerContext)}))
     [:div
      (merge
       {:ref (:popper-ref-cb popper)
        :class ["nosco-tooltip"]
        :style style}
       (js->clj (.. (:popper popper) -attributes -popper) :keywordize-keys true))
      [text/Text {:variant text/Body-Md-Regular}
       label]]]))

(def default-popper-opts
  {:placement "bottom"
   :flip true
   :offset [0 6]})

(defn use-tooltip
  "Returns a vector of [tooltip-props trigger-ref], where tooltip-props is a map of props
  to be passed to `Tooltip` and trigger-ref is a ref to be passed to the element which
  will trigger the tooltip on hover."
  ([] (use-tooltip nil))
  ([{:keys [popper-opts show-hide-delay-ms]
     :or {show-hide-delay-ms 250}}]
   (let [[visible ->visible] (hooks/useState false)
         [trigger-popper-ref-cb popper popper-update] (popup/use-popper (or popper-opts default-popper-opts))
         mouse-inside-ref (React/useRef false)
         trigger-ref (React/useRef nil)
         mounted? (util/use-mounted?)
         update-visibility-fn (util/use-debounce
                               show-hide-delay-ms
                               (fn []
                                 (when (mounted?)
                                   (->visible (.-current mouse-inside-ref)))))]
     (hooks/useLayoutEffect
      (fn []
        (when popper-update
          (popper-update))
        js/undefined)
      [visible])
     (hooks/useEffect
      (fn []
        (when-let [trigger-el (.-current trigger-ref)]
          (let [mouse-enter-fn (fn []
                                 (set! (.-current mouse-inside-ref) true)
                                 (update-visibility-fn))
                mouse-leave-fn (fn []
                                 (set! (.-current mouse-inside-ref) false)
                                 (update-visibility-fn))]
            (doto trigger-el
              (.addEventListener "mouseenter" mouse-enter-fn)
              (.addEventListener "mouseleave" mouse-leave-fn))
            (fn []
              (doto trigger-el
                (.removeEventListener "mouseenter" mouse-enter-fn)
                (.removeEventListener "mouseleave" mouse-leave-fn)))))))
     [{:visible visible :popper popper}
      (nosco-react/merge-refs trigger-ref trigger-popper-ref-cb)])))

(defnc WithTooltip
  "Takes a single element as children and uses that element as the trigger for a tooltip.
  If label is falsey, then the children are simply passed through.
  Throws with multiple or no children."
  [{:keys [label popper-opts show-hide-delay-ms tooltip-style children]
    :or {show-hide-delay-ms 250}}]
  {:pre [(React/isValidElement children)]}
  (let [[tooltip-props trigger-ref] (use-tooltip {:popper-opts popper-opts :show-hide-delay-ms show-hide-delay-ms})]
    (if-not label
      children
      [:<>
       (React/cloneElement children #js {:ref trigger-ref})
       [Tooltip (assoc tooltip-props
                       :label label
                       :tooltip-style tooltip-style)]])))
